import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import {
  Document,
  Font,
  Image,
  Page,
  PDFDownloadLink,
  PDFViewer,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { useContext } from "react";
import FeedbackContext from "../../context/cmscontext";
import { CAR_REPORT, CAR_REPORT_DETAIL } from "../../constant";
import { POST } from "../../constant/apiMethods";
import Labels from "../../services/apiLabels";
import config from "../../config";
import MontserratFont from "../../assets/fonts/Montserrat-Regular.ttf";
import MontserratFontBold from "../../assets/fonts/Montserrat-Bold.ttf";
import carDiagram from "../../assets/images/carnew.jpg";
import "./pdf.css";

const MAP_WIDTH = 553;
const MAP_HEIGHT = 406;
const MAP_DISPLAY_WIDTH = 520;
const EMPTY_COPY = {};

const DAMAGE_MARKER_WEIGHTS = {
  P: 1,
  D1: 2,
  D2: 3,
  D3: 4,
  A: 5,
  S: 4,
  W: 3,
  R: 2,
};

const MARKER_COLORS = {
  P: { bg: "#0284c7", ring: "#bae6fd" },
  D1: { bg: "#d97706", ring: "#fed7aa" },
  D2: { bg: "#ea580c", ring: "#fdba74" },
  D3: { bg: "#dc2626", ring: "#fecaca" },
  A: { bg: "#b91c1c", ring: "#fca5a5" },
  S: { bg: "#7c3aed", ring: "#ddd6fe" },
  W: { bg: "#475569", ring: "#cbd5e1" },
  R: { bg: "#2563eb", ring: "#bfdbfe" },
};

Font.register({
  family: "Montserrat",
  fonts: [
    { src: MontserratFont },
    { src: MontserratFontBold, fontWeight: "bold" },
  ],
});

function asList(raw) {
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  return [];
}

function hasText(value) {
  if (value == null) return false;
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value !== "string") return false;
  const text = value.trim();
  if (!text) return false;
  return !/^(none|n\/a|null|undefined|-)$/i.test(text);
}

function displayText(value) {
  if (!hasText(value)) return "";
  return String(value).trim();
}

function firstText(...values) {
  for (const value of values) {
    const text = displayText(value);
    if (text) return text;
  }
  return "";
}

function unwrapProduct(raw) {
  if (Array.isArray(raw)) return unwrapProduct(raw[0]);
  if (!raw || typeof raw !== "object") return {};
  const nested = raw.data;
  if (
    nested &&
    typeof nested === "object" &&
    !raw.product_name &&
    !raw.year &&
    (nested.product_name || nested.id || nested.year || Array.isArray(nested))
  ) {
    return unwrapProduct(nested);
  }
  return raw;
}

function vehicleNameFromSections(sections) {
  for (const section of sections || []) {
    for (const item of section?.inspection_title || []) {
      const selected = item?.selected_inspection_question?.[0];
      const name = firstText(selected?.car, selected?.product_name);
      if (name) return name;
    }
  }
  return "";
}

function photoUrl(item) {
  if (!item) return "";
  const value =
    typeof item === "string"
      ? item
      : item.photo_url ||
        item.photoUrl ||
        item.image ||
        item.src ||
        item.imgs ||
        item.img ||
        "";
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  const nested = trimmed.match(/^https?:\/\/[^/]+\/(https?:\/\/\S+)/i);
  const url = nested ? nested[1] : trimmed;
  return url.replace(/https:\/(?!\/)/i, "https://");
}

function isRemoteUrl(url) {
  return /^https?:\/\//i.test(url || "");
}

function bodyTypeLabel(value) {
  if (value == 1) return "Hatchback";
  if (value == 2) return "MPV";
  if (value == 3) return "SUV";
  if (value == 4) return "Saloon";
  const text = displayText(value);
  return text || "-";
}

function transmissionLabel(value) {
  if (value == 1) return "AUTO";
  if (value == 2) return "MANUAL";
  if (value == 3) return "BOTH";
  const text = displayText(value);
  return text || "-";
}

function roundScore(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 10) / 10;
}

function markerKey(value) {
  return String(value || "").trim().toUpperCase();
}

function markerColor(symbol) {
  return MARKER_COLORS[markerKey(symbol)] || MARKER_COLORS.R;
}

function scoreTone(score) {
  const n = Number(score) || 0;
  if (n >= 90) {
    return { key: "excellent", label: "Excellent", color: "#059669", soft: "#ecfdf5" };
  }
  if (n >= 75) {
    return { key: "good", label: "Good", color: "#2563eb", soft: "#eff6ff" };
  }
  if (n >= 60) {
    return { key: "fair", label: "Fair", color: "#d97706", soft: "#fffbeb" };
  }
  return { key: "poor", label: "Needs work", color: "#dc2626", soft: "#fef2f2" };
}

function questionPenalty(question) {
  const selected = question?.selected_inspection_question?.[0];
  const raw = selected?.damage ?? question?.count ?? 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function sectionScoreFromFormula(section) {
  const questions = Array.isArray(section?.inspection_title)
    ? section.inspection_title
    : [];
  const penalty = questions.reduce(
    (sum, question) => sum + questionPenalty(question),
    0
  );
  return {
    title: section?.title || "Section",
    score: roundScore(Math.max(0, Math.min(100, 100 - penalty))),
  };
}

function markerWeight(point) {
  const key = markerKey(point?.symbol);
  if (Object.prototype.hasOwnProperty.call(DAMAGE_MARKER_WEIGHTS, key)) {
    return DAMAGE_MARKER_WEIGHTS[key];
  }
  return 2;
}

function buildLegend(apiTypes, points) {
  const bySymbol = new Map();
  (Array.isArray(apiTypes) ? apiTypes : []).forEach((item) => {
    const symbol = markerKey(item?.symbol);
    if (!symbol) return;
    bySymbol.set(symbol, {
      symbol,
      title: item?.title || symbol,
      count: 0,
    });
  });
  (Array.isArray(points) ? points : []).forEach((item) => {
    const symbol = markerKey(item?.symbol);
    if (!symbol) return;
    const current = bySymbol.get(symbol) || {
      symbol,
      title: item?.title || symbol,
      count: 0,
    };
    current.count += 1;
    if (item?.title && current.title === symbol) current.title = item.title;
    bySymbol.set(symbol, current);
  });
  return [...bySymbol.values()];
}

function overallFromSections(sections, points) {
  const parts = (Array.isArray(sections) ? sections : []).map(
    sectionScoreFromFormula
  );
  const list = Array.isArray(points) ? points : [];
  if (list.length) {
    const penalty = list.reduce((sum, point) => sum + markerWeight(point), 0);
    parts.push({
      title: "Damage map",
      score: roundScore(Math.max(0, Math.min(100, 100 - penalty))),
    });
  }
  if (!parts.length) return { score: 0, parts };
  return {
    score: roundScore(
      parts.reduce((sum, part) => sum + part.score, 0) / parts.length
    ),
    parts,
  };
}

function pinOffset(value, total, mappedSize) {
  const str = String(value ?? "").trim();
  if (str.endsWith("%")) {
    const n = parseFloat(str);
    return Number.isFinite(n) ? (n / 100) * mappedSize : 0;
  }
  const n = parseFloat(str);
  if (!Number.isFinite(n) || !total) return 0;
  return (n / total) * mappedSize;
}

function bytesToDataUrl(bytes, mime) {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return `data:${mime};base64,${btoa(binary)}`;
}

function sniffImageMime(bytes) {
  if (!bytes || bytes.length < 12) return "";
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return "image/jpeg";
  }
  if (
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47
  ) {
    return "image/png";
  }
  if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
    return "image/gif";
  }
  if (
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return "image/webp";
  }
  return "";
}

function s3ProxyUrl(url) {
  try {
    const parsed = new URL(url);
    if (!/amazonaws\.com/i.test(parsed.hostname)) return "";
    return true;
  } catch (e) {
    return "";
  }
}

function fetchUrlAsDataUrl(url, ms = 20000) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  return fetch(url, {
    signal: ctrl.signal,
    cache: "no-store",
    credentials: "same-origin",
    headers: { Accept: "image/jpeg,image/png,image/*,application/octet-stream" },
  })
    .then((res) => (res.ok ? res.arrayBuffer() : Promise.reject()))
    .then((buffer) => {
      const bytes = new Uint8Array(buffer);
      const mime = sniffImageMime(bytes);
      if (!mime) return null;
      return bytesToDataUrl(bytes, mime);
    })
    .catch(() => null)
    .finally(() => clearTimeout(timer));
}

function fetchDataUrl(url) {
  if (!s3ProxyUrl(url)) return fetchUrlAsDataUrl(url);
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 20000);
  const toDataUrl = (res) =>
    res.ok
      ? res.arrayBuffer().then((buffer) => {
          const bytes = new Uint8Array(buffer);
          const mime = sniffImageMime(bytes);
          return mime ? bytesToDataUrl(bytes, mime) : Promise.reject();
        })
      : Promise.reject();
  return fetch("/s3-image", {
    method: "POST",
    signal: ctrl.signal,
    cache: "no-store",
    credentials: "same-origin",
    headers: {
      Accept: "image/jpeg,image/png,application/octet-stream",
      "Content-Type": "application/json",
      "X-S3-Url": url,
    },
    body: JSON.stringify({ url }),
  })
    .then(toDataUrl)
    .catch(() =>
      fetch(`/s3-image?u=${encodeURIComponent(url)}`, {
        method: "GET",
        signal: ctrl.signal,
        cache: "no-store",
        credentials: "same-origin",
        headers: {
          Accept: "image/jpeg,image/png,application/octet-stream",
        },
      }).then(toDataUrl)
    )
    .catch(() => null)
    .finally(() => clearTimeout(timer));
}

function resolvedSrc(url, imageMap, fallback, index) {
  if (index != null && imageMap[`point-${index}`]) return imageMap[`point-${index}`];
  if (url && imageMap[url]) return imageMap[url];
  if (url && !isRemoteUrl(url)) return url;
  return fallback || null;
}

function pdfImageSource(src) {
  if (!src) return null;
  return src;
}

const styles = StyleSheet.create({
  page: {
    fontFamily: "Montserrat",
    paddingTop: 28,
    paddingBottom: 42,
    paddingHorizontal: 22,
    color: "#0f172a",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  kicker: {
    fontSize: 9,
    color: "#64748b",
    marginBottom: 2,
  },
  title: {
    fontSize: 14,
    fontWeight: "bold",
  },
  logo: {
    width: 72,
    height: 36,
    objectFit: "contain",
  },
  scoreHero: {
    flexDirection: "row",
    alignItems: "stretch",
    marginTop: 4,
    marginBottom: 10,
    gap: 14,
  },
  scoreDial: {
    width: 118,
    minHeight: 118,
    paddingVertical: 12,
    paddingHorizontal: 10,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
  },
  scoreDialValue: {
    fontSize: 24,
    fontWeight: "bold",
    lineHeight: 1.1,
  },
  scoreDialPct: {
    fontSize: 10,
    marginTop: 1,
    color: "#64748b",
  },
  scoreDialBadge: {
    marginTop: 8,
    fontSize: 8,
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  scoreMain: {
    flex: 1,
    paddingTop: 2,
  },
  scoreLead: {
    fontSize: 8,
    color: "#64748b",
    lineHeight: 1.45,
    marginBottom: 8,
  },
  scoreTileGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginHorizontal: -4,
  },
  scoreTile: {
    width: "50%",
    paddingHorizontal: 4,
    paddingBottom: 8,
  },
  scoreTileInner: {
    borderWidth: 1,
    borderColor: "#e2e8f0",
    borderRadius: 12,
    paddingVertical: 7,
    paddingHorizontal: 8,
  },
  scoreTileHead: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 5,
    gap: 6,
  },
  scoreTileTitle: {
    fontSize: 8,
    color: "#475569",
  },
  scoreTileValue: {
    fontSize: 9,
    fontWeight: "bold",
  },
  infoRow: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: "#e2e8f0",
    marginTop: 10,
    marginBottom: 14,
  },
  infoCell: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 6,
    alignItems: "center",
    borderRightWidth: 1,
    borderRightColor: "#e2e8f0",
  },
  infoCellLast: {
    borderRightWidth: 0,
  },
  infoValue: {
    fontSize: 8,
    fontWeight: "bold",
  },
  infoLabel: {
    fontSize: 6,
    color: "#64748b",
    marginTop: 2,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: "bold",
    marginBottom: 8,
    marginTop: 6,
  },
  photoGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 8,
  },
  photoCell: {
    width: "33.33%",
    padding: 4,
  },
  photo: {
    width: "100%",
    height: 86,
    objectFit: "cover",
  },
  mapWrap: {
    width: MAP_DISPLAY_WIDTH,
    height: (MAP_DISPLAY_WIDTH / MAP_WIDTH) * MAP_HEIGHT,
    position: "relative",
    marginBottom: 12,
  },
  mapImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  pinShell: {
    position: "absolute",
    width: 22,
    height: 22,
    borderRadius: 999,
    borderWidth: 1.5,
    borderColor: "#ffffff",
    alignItems: "center",
    justifyContent: "center",
  },
  pinText: {
    fontSize: 7,
    fontWeight: "bold",
    color: "#ffffff",
  },
  legendGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginHorizontal: -4,
  },
  legendType: {
    width: "50%",
    paddingHorizontal: 4,
    paddingBottom: 8,
  },
  legendTypeInner: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#e2e8f0",
    borderRadius: 12,
    paddingVertical: 7,
    paddingHorizontal: 8,
    minHeight: 46,
  },
  legendSymbolWrap: {
    width: 24,
    height: 24,
    borderRadius: 999,
    marginRight: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  legendSymbolText: {
    fontSize: 7,
    fontWeight: "bold",
    color: "#fff",
  },
  legendTextWrap: {
    flex: 1,
  },
  legendName: {
    fontSize: 8,
    fontWeight: "bold",
    color: "#0f172a",
  },
  legendMeta: {
    fontSize: 7,
    color: "#64748b",
    marginTop: 1,
  },
  legendItem: {
    width: "33.33%",
    padding: 4,
    marginBottom: 6,
  },
  legendPhoto: {
    width: "100%",
    height: 72,
    objectFit: "cover",
    marginBottom: 4,
  },
  legendLabel: {
    fontSize: 8,
  },
  damagePhotoGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginHorizontal: -4,
  },
  damagePhotoCell: {
    width: "50%",
    paddingHorizontal: 4,
    paddingBottom: 8,
  },
  damagePhotoInner: {
    borderWidth: 1,
    borderColor: "#e2e8f0",
    borderRadius: 12,
    overflow: "hidden",
    backgroundColor: "#fff",
  },
  damagePhotoImage: {
    width: "100%",
    height: 112,
    objectFit: "cover",
  },
  damagePhotoMissing: {
    width: "100%",
    height: 112,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f1f5f9",
  },
  damagePhotoLabel: {
    fontSize: 8,
    paddingVertical: 6,
    paddingHorizontal: 8,
    color: "#334155",
  },
  barBlock: {
    marginBottom: 8,
  },
  barHead: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 3,
  },
  barLabel: {
    fontSize: 9,
    fontWeight: "bold",
  },
  track: {
    height: 6,
    backgroundColor: "#e2e8f0",
    borderRadius: 3,
  },
  fill: {
    height: 6,
    backgroundColor: "#2563eb",
    borderRadius: 3,
  },
  tinyTrack: {
    height: 5,
    backgroundColor: "#e2e8f0",
    borderRadius: 999,
    overflow: "hidden",
  },
  tinyFill: {
    height: 5,
    borderRadius: 999,
  },
  tableHead: {
    flexDirection: "row",
    backgroundColor: "#475569",
    color: "#fff",
    paddingVertical: 6,
    paddingHorizontal: 8,
    marginTop: 10,
  },
  tableHeadText: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#fff",
  },
  sectionPctWrap: {
    width: "30%",
    alignItems: "flex-end",
  },
  sectionPctValue: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#fff",
    marginBottom: 4,
  },
  sectionPctTrack: {
    width: 72,
    height: 5,
    backgroundColor: "rgba(255,255,255,0.28)",
    borderRadius: 999,
    overflow: "hidden",
  },
  sectionPctFill: {
    height: 5,
    borderRadius: 999,
  },
  row: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: "#e2e8f0",
    borderTopWidth: 0,
  },
  cell: {
    width: "50%",
    paddingVertical: 6,
    paddingHorizontal: 8,
    fontSize: 8,
  },
  cellRight: {
    borderLeftWidth: 1,
    borderLeftColor: "#e2e8f0",
  },
  findingPhotos: {
    flexDirection: "row",
    flexWrap: "wrap",
    padding: 4,
    borderWidth: 1,
    borderColor: "#e2e8f0",
    borderTopWidth: 0,
  },
  findingPhoto: {
    width: 90,
    height: 64,
    margin: 4,
    objectFit: "cover",
  },
  muted: {
    fontSize: 9,
    color: "#64748b",
  },
  footer: {
    position: "absolute",
    left: 22,
    right: 22,
    bottom: 16,
    fontSize: 8,
    color: "#94a3b8",
    textAlign: "center",
  },
});

function ReportPdfDocument({
  id,
  carInfo,
  damagePoints,
  legendEntries,
  sections,
  copy,
  imageMap,
  overall,
}) {
  const mapHeight = (MAP_DISPLAY_WIDTH / MAP_WIDTH) * MAP_HEIGHT;
  const overallTone = scoreTone(overall.score);
  const brandSrc = resolvedSrc(carInfo?.brands_image, imageMap);
  const title =
    [displayText(carInfo?.year), displayText(carInfo?.product_name)]
      .filter(Boolean)
      .join(" ") || `Lot #${id}`;

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        <View style={styles.header}>
          <View>
            <Text style={styles.kicker}>Condition report</Text>
            <Text style={styles.title}>{title}</Text>
            <Text style={styles.kicker}>Lot reference #{id}</Text>
          </View>
          {brandSrc ? <Image src={pdfImageSource(brandSrc)} style={styles.logo} /> : null}
        </View>

        <View style={styles.infoRow}>
          <View style={styles.infoCell}>
            <Text style={styles.infoValue}>
              {displayText(carInfo?.Mileage) || "-"}
            </Text>
            <Text style={styles.infoLabel}>(KM)</Text>
          </View>
          <View style={styles.infoCell}>
            <Text style={styles.infoValue}>
              {bodyTypeLabel(carInfo?.car_body_type)}
            </Text>
            <Text style={styles.infoLabel}>Body</Text>
          </View>
          <View style={styles.infoCell}>
            <Text style={styles.infoValue}>
              {displayText(carInfo?.fuel_type) || "-"}
            </Text>
            <Text style={styles.infoLabel}>Fuel</Text>
          </View>
          <View style={[styles.infoCell, styles.infoCellLast]}>
            <Text style={styles.infoValue}>
              {transmissionLabel(carInfo?.Transmission)}
            </Text>
            <Text style={styles.infoLabel}>Transmission</Text>
          </View>
        </View>

        <Text style={styles.sectionTitle}>
          {copy.report_text || "Damage map"}
        </Text>
        <View style={styles.mapWrap}>
          <Image src={pdfImageSource(carDiagram)} style={styles.mapImage} />
          {damagePoints.map((item, index) => {
            const color = markerColor(item.symbol);
            return (
              <View
                key={`pin-${index}`}
                style={[
                  styles.pinShell,
                  {
                    top: pinOffset(item.top, MAP_HEIGHT, mapHeight) - 5,
                    left: pinOffset(item.left, MAP_WIDTH, MAP_DISPLAY_WIDTH) - 6,
                    backgroundColor: color.bg,
                    borderColor: color.ring,
                  },
                ]}
              >
                <Text style={styles.pinText}>{item.symbol || ""}</Text>
              </View>
            );
          })}
        </View>
        {!damagePoints.length ? (
          <Text style={styles.muted}>No damage markers recorded.</Text>
        ) : null}

        <Text style={styles.sectionTitle}>Overall inspection score</Text>
        <View style={styles.scoreHero}>
          <View
            style={[
              styles.scoreDial,
              {
                backgroundColor: overallTone.soft,
                borderColor: overallTone.color,
              },
            ]}
          >
            <Text style={[styles.scoreDialValue, { color: overallTone.color }]}>
              {overall.score}
            </Text>
            <Text style={styles.scoreDialPct}>percent</Text>
            <Text style={[styles.scoreDialBadge, { color: overallTone.color }]}>
              {overallTone.label}
            </Text>
          </View>
          <View style={styles.scoreMain}>
            <Text style={styles.scoreLead}>
              This lot scores {overall.score}% overall across inspection
              sections{damagePoints.length ? " and the damage map." : "."}
            </Text>
            <View style={styles.scoreTileGrid}>
              {overall.parts.map((part, index) => {
                const tone = scoreTone(part.score);
                return (
                  <View key={`bar-${index}`} style={styles.scoreTile}>
                    <View
                      style={[
                        styles.scoreTileInner,
                        { backgroundColor: tone.soft },
                      ]}
                    >
                      <View style={styles.scoreTileHead}>
                        <Text style={styles.scoreTileTitle}>{part.title}</Text>
                        <Text
                          style={[styles.scoreTileValue, { color: tone.color }]}
                        >
                          {`${part.score}%`}
                        </Text>
                      </View>
                      <View style={styles.tinyTrack}>
                        <View
                          style={[
                            styles.tinyFill,
                            {
                              width: `${part.score}%`,
                              backgroundColor: tone.color,
                            },
                          ]}
                        />
                      </View>
                    </View>
                  </View>
                );
              })}
            </View>
          </View>
        </View>

        {legendEntries.length ? (
          <View>
            <Text style={styles.sectionTitle}>
              {copy.car_legend_text || "Damage types"}
            </Text>
            <View style={styles.legendGrid}>
              {legendEntries.map((entry) => {
                const color = markerColor(entry.symbol);
                return (
                  <View key={`legend-type-${entry.symbol}`} style={styles.legendType}>
                    <View style={styles.legendTypeInner}>
                      <View
                        style={[
                          styles.legendSymbolWrap,
                          { backgroundColor: color.bg },
                        ]}
                      >
                        <Text style={styles.legendSymbolText}>
                          {entry.symbol}
                        </Text>
                      </View>
                      <View style={styles.legendTextWrap}>
                        <Text style={styles.legendName}>{entry.title}</Text>
                        <Text style={styles.legendMeta}>
                          {entry.count > 0
                            ? `${entry.count} on map`
                            : "Not on this lot"}
                        </Text>
                      </View>
                    </View>
                  </View>
                );
              })}
            </View>
          </View>
        ) : null}

        {damagePoints.length ? (
          <View>
            <Text style={styles.sectionTitle}>Damage photos</Text>
            <View style={styles.damagePhotoGrid}>
              {damagePoints.map((item, index) => {
                const src = resolvedSrc(photoUrl(item), imageMap, null, index);
                return (
                  <View key={`legend-${index}`} style={styles.damagePhotoCell}>
                    <View style={styles.damagePhotoInner}>
                      {src ? (
                        <Image
                          src={pdfImageSource(src)}
                          style={styles.damagePhotoImage}
                        />
                      ) : (
                        <View style={styles.damagePhotoMissing}>
                          <Text style={styles.muted}>Photo unavailable</Text>
                        </View>
                      )}
                      <Text style={styles.damagePhotoLabel}>
                        {`${item.symbol || ""}: ${item.title || ""}`.trim()}
                      </Text>
                    </View>
                  </View>
                );
              })}
            </View>
          </View>
        ) : null}

        {sections.map((section, sectionIndex) => {
          const questions = Array.isArray(section?.inspection_title)
            ? section.inspection_title
            : [];
          const part = overall.parts.find(
            (item) => item.title === section.title
          );
          const sectionScore =
            part?.score ?? roundScore(section?.inspect_count);
          const tone = scoreTone(sectionScore);
          return (
            <View key={`section-${sectionIndex}`}>
              <View style={styles.tableHead}>
                <Text style={[styles.tableHeadText, { width: "70%" }]}>
                  {section.title || "Section"}
                </Text>
                <View style={styles.sectionPctWrap}>
                  <Text style={styles.sectionPctValue}>{`${sectionScore}%`}</Text>
                  <View style={styles.sectionPctTrack}>
                    <View
                      style={[
                        styles.sectionPctFill,
                        {
                          width: `${sectionScore}%`,
                          backgroundColor: tone.color,
                        },
                      ]}
                    />
                  </View>
                </View>
              </View>
              {questions.map((question, questionIndex) => {
                const selected = question?.selected_inspection_question?.[0];
                const photos = (
                  Array.isArray(selected?.images_) ? selected.images_ : []
                )
                  .map((image) => resolvedSrc(photoUrl(image), imageMap))
                  .filter(Boolean);
                return (
                  <View key={`q-${sectionIndex}-${questionIndex}`}>
                    <View style={styles.row}>
                      <Text style={styles.cell}>{question?.question || "-"}</Text>
                      <Text style={[styles.cell, styles.cellRight]}>
                        {selected?.choice_select_ || "-"}
                      </Text>
                    </View>
                    {photos.length ? (
                      <View style={styles.findingPhotos}>
                        {photos.map((src, photoIndex) => (
                          <Image
                            key={`qimg-${photoIndex}`}
                            src={pdfImageSource(src)}
                            style={styles.findingPhoto}
                          />
                        ))}
                      </View>
                    ) : null}
                  </View>
                );
              })}
            </View>
          );
        })}

        <Text style={styles.footer} fixed>
          All Rights Reserved, Sell Any Wheel
        </Text>
      </Page>
    </Document>
  );
}

export const Report = ({
  car_report_detail_data_new,
  car_report_data,
  car_report_detail_data,
  winerListloading,
  car_report_detail_call,
  car_report_call,
  loading,
}) => {
  const { feedback } = useContext(FeedbackContext) || {};
  const history = useHistory();
  const { id } = useParams();
  const [imageMap, setImageMap] = useState({});
  const [imagesReady, setImagesReady] = useState(false);
  const [legendTypes, setLegendTypes] = useState([]);
  const [productDetail, setProductDetail] = useState(null);
  const [detailReady, setDetailReady] = useState(false);

  const copy = feedback?.car_report_page || EMPTY_COPY;
  const damagePoints = useMemo(
    () => asList(car_report_data),
    [car_report_data]
  );
  const sections = useMemo(
    () => asList(car_report_detail_data ?? car_report_detail_data_new),
    [car_report_detail_data, car_report_detail_data_new]
  );
  const carInfo = useMemo(() => {
    const fromDetail = unwrapProduct(productDetail);
    const reportPayload =
      car_report_detail_data_new &&
      typeof car_report_detail_data_new === "object" &&
      !Array.isArray(car_report_detail_data_new)
        ? car_report_detail_data_new
        : {};
    const fromReport = unwrapProduct(
      reportPayload.car_info &&
        typeof reportPayload.car_info === "object" &&
        !Array.isArray(reportPayload.car_info)
        ? reportPayload.car_info
        : {}
    );
    const brandModel = [
      firstText(fromDetail.brands, fromReport.brands),
      firstText(fromDetail.model_name, fromReport.model_name),
    ]
      .filter(Boolean)
      .join(" ");
    return {
      product_name: firstText(
        fromDetail.product_name,
        fromReport.product_name,
        vehicleNameFromSections(sections),
        brandModel
      ),
      year: firstText(fromDetail.year, fromReport.year),
      Mileage: firstText(
        fromDetail.Mileage,
        fromDetail.mileage,
        fromReport.Mileage,
        fromReport.mileage
      ),
      fuel_type: firstText(fromDetail.fuel_type, fromReport.fuel_type),
      Transmission: firstText(
        fromDetail.Transmission,
        fromDetail.transmission,
        fromReport.Transmission,
        fromReport.transmission
      ),
      car_body_type: firstText(
        fromDetail.car_body_type,
        fromReport.car_body_type
      ),
      brands_image: firstText(
        fromDetail.brands_image,
        fromReport.brands_image
      ),
    };
  }, [car_report_detail_data_new, productDetail, sections]);
  const dataReady = Boolean(id) && !winerListloading && !loading && detailReady;
  const hasContent = damagePoints.length > 0 || sections.length > 0;
  const overall = useMemo(() => {
    const breakdown = overallFromSections(sections, damagePoints);
    if (breakdown.parts.length) return breakdown;
    return {
      score: roundScore(car_report_detail_data_new?.total_inspect),
      parts: [],
    };
  }, [sections, damagePoints, car_report_detail_data_new]);
  const legendEntries = useMemo(
    () => buildLegend(legendTypes, damagePoints),
    [legendTypes, damagePoints]
  );

  useEffect(() => {
    if (!id) return;
    car_report_call({ id });
    car_report_detail_call({ id });
  }, [id]);

  useEffect(() => {
    if (!id) {
      setProductDetail(null);
      setDetailReady(true);
      return undefined;
    }
    let cancelled = false;
    setDetailReady(false);
    setProductDetail(null);
    fetch(`${config.baseUrl}${Labels.product_detail}${id}/`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => {
        if (cancelled) return;
        const product = unwrapProduct(data);
        setProductDetail(
          firstText(product.product_name, product.id, product.year) ||
            product.Mileage != null ||
            product.mileage != null
            ? product
            : null
        );
      })
      .catch(() => {
        if (!cancelled) setProductDetail(null);
      })
      .finally(() => {
        if (!cancelled) setDetailReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${config.baseUrl}api/show_legend_car/`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => {
        if (cancelled) return;
        setLegendTypes(asList(data?.data ?? data));
      })
      .catch(() => {
        if (!cancelled) setLegendTypes([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!dataReady) {
      setImagesReady(false);
      return undefined;
    }
    let cancelled = false;
    setImagesReady(false);
    Promise.all(
      damagePoints.map((item, index) => {
        const url = photoUrl(item);
        if (!isRemoteUrl(url)) return Promise.resolve([index, url, null]);
        return fetchDataUrl(url).then((data) => [index, url, data]);
      })
    )
      .then((entries) => {
        if (cancelled) return;
        const next = {};
        entries.forEach(([index, url, data]) => {
          if (!data) return;
          next[`point-${index}`] = data;
          if (url) next[url] = data;
        });
        setImageMap(next);
        setImagesReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          setImageMap({});
          setImagesReady(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [dataReady, damagePoints]);

  const documentReady = dataReady && imagesReady && hasContent;
  const fileName = `condition-report-${id || "lot"}.pdf`;
  const pdfDocument = useMemo(() => {
    if (!documentReady) return null;
    return (
      <ReportPdfDocument
        id={id}
        carInfo={carInfo}
        damagePoints={damagePoints}
        legendEntries={legendEntries}
        sections={sections}
        copy={copy}
        imageMap={imageMap}
        overall={overall}
      />
    );
  }, [
    documentReady,
    id,
    carInfo,
    damagePoints,
    legendEntries,
    sections,
    copy,
    imageMap,
    overall,
  ]);

  return (
    <div className="dd-pdf-page">
      <header className="dd-pdf-toolbar">
        <div>
          <h1>Vehicle condition report</h1>
          <p>{id ? `Lot #${id}` : "Missing lot reference"}</p>
        </div>
        <div className="dd-pdf-actions">
          <button
            type="button"
            className="dd-pdf-btn"
            onClick={() => {
              if (window.history.length > 1) history.goBack();
              else window.close();
            }}
          >
            Close
          </button>
          {documentReady ? (
            <PDFDownloadLink
              document={pdfDocument}
              fileName={fileName}
              className="dd-pdf-btn dd-pdf-btn--primary"
            >
              {({ loading: downloading }) =>
                downloading ? "Preparing PDF…" : "Download PDF"
              }
            </PDFDownloadLink>
          ) : (
            <span className="dd-pdf-btn dd-pdf-btn--primary" aria-disabled="true">
              Download PDF
            </span>
          )}
        </div>
      </header>

      {!id ? (
        <div className="dd-pdf-status">
          <h2>Missing lot</h2>
          <p>This PDF link does not include a vehicle id.</p>
        </div>
      ) : !dataReady || !imagesReady ? (
        <div className="dd-pdf-status">
          <h2>Generating PDF…</h2>
          <p>Loading inspection data and photos for this lot.</p>
        </div>
      ) : !hasContent ? (
        <div className="dd-pdf-status">
          <h2>No inspection to export</h2>
          <p>
            This lot has no checklist results or damage markers, so a report
            PDF cannot be generated yet.
          </p>
        </div>
      ) : (
        <div className="dd-pdf-viewer">
          <PDFViewer width="100%" height="100%" showToolbar>
            {pdfDocument}
          </PDFViewer>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  car_report_data: state.carreport.car_report_data,
  car_report_detail_data: state.carreport.car_report_detail_data?.data,
  car_report_detail_data_new: state.carreport.car_report_detail_data,
  loading: state.carreport.loading,
  winerListloading: state.carreport.winerListloading,
});

const mapDispatchToProps = (dispatch) => ({
  car_report_call: (data) =>
    dispatch({
      type: CAR_REPORT,
      payload: {
        type: CAR_REPORT,
        method: POST,
        label: Labels.car_report,
        hideOverlayLoader: true,
        data,
      },
    }),
  car_report_detail_call: (data) =>
    dispatch({
      type: CAR_REPORT_DETAIL,
      payload: {
        type: CAR_REPORT_DETAIL,
        method: POST,
        label: Labels.car_report_detail,
        hideOverlayLoader: true,
        data,
      },
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(Report);
